import { useMemo } from 'react';

import { useParams } from 'react-router';

import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { Field, FormikProps, getIn } from 'formik';
import * as Yup from 'yup';

import { GrupoEconomicoAutoComplete, GrupoUsuarioAutoComplete, StoreAutoComplete, TipoUsuarioAutoComplete } from 'components/autocompletes';
import { CargoFuncionarioAutoComplete } from 'components/autocompletes/CargoFuncionarioAutoComplete';
import { LocalFuncionarioAutoComplete } from 'components/autocompletes/LocalFuncionarioAutoComplete';
import { TipoEntregaAutoComplete } from 'components/autocompletes/TipoEntregaAutoComplete';
import { CissMaskField, CissNumberField, CissPrimaryKeyField, CissTextField } from 'components/fields/';
import { Row } from 'components/form';
import { Block, FormPage } from 'components/page';
import { SubTitle } from 'components/text';
import { useClientContext } from 'context/ClientContext';
import { useLoaderContext } from 'context/LoaderContext';
import { FilterValuesProps } from 'hooks/useFilter';
import { CepField } from 'pages/cadastro/usuario/components';
import { useMutateUsuario, useQueryGrupoUsuarioPadrao } from 'pages/cadastro/usuario/hooks';
import { GruposEconomicosProps, initialUsuarioValues, UsersOptionsProps, UsuarioProps } from 'pages/cadastro/usuario/types';

/**
 * Retorna filtro de grupo econômico para listagem de lojas.
 */
const getFilterLojaGrupoEconomico = (gruposEconomicos: GruposEconomicosProps[]): FilterValuesProps[] => [
    {
        property: 'idGrupoEconomico',
        value: gruposEconomicos?.map((grupo) => grupo.idGrupoEconomico),
        operator: 'in',
        logicalOperator: 'AND',
    },
];

/**
 * Formata os campos de telefone e celular, para caso tiverem mais de 11 números, pegar os últimos 11 digitos
 */
const mapContentToInitialValues = (content: UsuarioProps) => {
    if (content?.dsCelular && content?.dsCelular?.length > 11) {
        content.dsCelular = content.dsCelular.slice(-11);
    }

    if (content?.dsFone && content?.dsFone?.length > 11) {
        content.dsFone = content.dsFone.slice(-11);
    }

    return content;
};

export function UsuarioFormComp() {
    const { id } = useParams();
    const { tipoPedido } = useClientContext();
    const { handleSubmit, isLoading } = useMutateUsuario();
    const { data: dataPermissoesPadrao } = useQueryGrupoUsuarioPadrao();
    const isTipoPedidoFuncionario = tipoPedido === 'funcionario';

    useLoaderContext(isLoading);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                dsUsuario: Yup.string().required(),
                dsLogin: Yup.string().required(),
                tipoUsuario: Yup.object().required(),
                dsCelular: Yup.string().min(11).nullable(),
                dsFone: Yup.string().min(10).nullable(),
                usuarioAcessoGrupoEconomicos: Yup.array().when('tipoUsuario', {
                    is: (tipoUsuario: UsersOptionsProps) => tipoUsuario?.tipoUsuarioEnum === 'FRANQUEADO',
                    then: (schema) => schema.min(1, 'É necessário informar ao mínimo 1 Grupo Econômico'),
                }),
                loja: Yup.object().required(),
                usuarioFuncionario: Yup.object().when('tipoUsuario', {
                    is: (tipoUsuario: UsersOptionsProps) => tipoUsuario?.tipoUsuarioEnum === 'FUNCIONARIO',
                    then: (schema) =>
                        schema.shape({
                            cargo: Yup.object().required(),
                            tipoEntrega: Yup.object().required(),
                            localFuncionario: Yup.object().required(),
                        }),
                    otherwise: (schema) =>
                        schema.shape({
                            cargo: Yup.object().nullable(),
                            tipoEntrega: Yup.object().nullable(),
                            localFuncionario: Yup.object().nullable(),
                        }),
                }),
            }),
        [],
    );

    return (
        <FormPage
            title="Usuário"
            url="/gestao/usuario"
            values={initialUsuarioValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            mapContentToInitialValues={mapContentToInitialValues}
            goBackButton
            fgAtivo
        >
            {(formik: FormikProps<UsuarioProps>) => {
                const usuarioAcessoGrupoEconomicos = formik.values.usuarioAcessoGrupoEconomicos;
                const isFranqueado = formik.values.tipoUsuario?.tipoUsuarioEnum === 'FRANQUEADO';
                const isFuncionario = formik.values.tipoUsuario?.tipoUsuarioEnum === 'FUNCIONARIO';

                return (
                    <>
                        <Block>
                            <Row>
                                <Grid>
                                    <CissPrimaryKeyField value={formik.values.idUsuario} name="idUsuario" />
                                </Grid>

                                <Grid sx={{ flex: 2 }}>
                                    <CissTextField
                                        label="Nome completo"
                                        name="dsUsuario"
                                        value={formik.values.dsUsuario}
                                        error={formik.errors.dsUsuario}
                                        disabled={isTipoPedidoFuncionario}
                                        onChange={(e) => formik.setFieldValue('dsUsuario', e.target.value)}
                                    />
                                </Grid>

                                <Grid sx={{ flex: 1 }}>
                                    <CissTextField
                                        label="Login"
                                        value={formik.values.dsLogin}
                                        error={formik.errors.dsLogin}
                                        disabled={isTipoPedidoFuncionario || Boolean(id)}
                                        onChange={(e) => formik.setFieldValue('dsLogin', e.target.value)}
                                    />
                                </Grid>
                            </Row>

                            <Row>
                                <Grid sx={{ flex: 1 }}>
                                    <CissTextField
                                        label="Código Externo"
                                        name="cdUsuario"
                                        value={formik.values.cdUsuario}
                                        error={formik.errors.cdUsuario}
                                        disabled={isTipoPedidoFuncionario}
                                        onChange={(e) => formik.setFieldValue('cdUsuario', e.target.value)}
                                    />
                                </Grid>

                                <Grid sx={{ flex: 1 }}>
                                    <CissTextField
                                        label="E-mail"
                                        name="dsEmail"
                                        value={formik.values.dsEmail}
                                        error={formik.errors.dsEmail}
                                        disabled={isTipoPedidoFuncionario}
                                        onChange={(e) => formik.setFieldValue('dsEmail', e.target.value || null)}
                                    />
                                </Grid>

                                <Grid sx={{ flex: 1 }}>
                                    <CissMaskField
                                        label="Telefone"
                                        name="dsFone"
                                        format="phone"
                                        value={formik.values.dsFone}
                                        error={formik.errors.dsFone}
                                        disabled={isTipoPedidoFuncionario}
                                        onChange={(e) => formik.setFieldValue('dsFone', e.target.value || null)}
                                    />
                                </Grid>

                                <Grid sx={{ flex: 1 }}>
                                    <CissMaskField
                                        label="Celular/WhatsApp"
                                        name="dsCelular"
                                        format="phone"
                                        value={formik.values.dsCelular}
                                        error={formik.errors.dsCelular}
                                        disabled={isTipoPedidoFuncionario}
                                        onChange={(e) => formik.setFieldValue('dsCelular', e.target.value || null)}
                                    />
                                </Grid>
                            </Row>

                            <Row>
                                <Grid sx={{ flex: 1 }}>
                                    <CepField value={formik.values.cep?.nrCep || ''} disabled={isTipoPedidoFuncionario} />
                                </Grid>

                                <Grid sx={{ flex: 2 }}>
                                    <CissTextField
                                        label="Endereço"
                                        name="dsCelular"
                                        value={formik.values.dsEndereco}
                                        error={formik.errors.dsEndereco}
                                        disabled={isTipoPedidoFuncionario || !formik.values.cep?.cidade?.dsCidade}
                                        onChange={(e) => formik.setFieldValue('dsEndereco', e.target.value || null)}
                                    />
                                </Grid>

                                <Grid sx={{ flex: 1 }}>
                                    <CissNumberField
                                        label="Número"
                                        name="nrEndereco"
                                        preventEmptyField={false}
                                        value={formik.values.nrEndereco}
                                        error={formik.errors.nrEndereco}
                                        disabled={isTipoPedidoFuncionario || !formik.values.cep?.cidade?.dsCidade}
                                        onChange={(e) => formik.setFieldValue('nrEndereco', e.target.value || null)}
                                    />
                                </Grid>

                                <Grid sx={{ flex: 1 }}>
                                    <CissTextField
                                        label="Complemento"
                                        name="dsComplemento"
                                        value={formik.values.dsComplemento}
                                        error={formik.errors.dsComplemento}
                                        disabled={isTipoPedidoFuncionario || !formik.values.cep?.cidade?.dsCidade}
                                        onChange={(e) => formik.setFieldValue('dsComplemento', e.target.value || null)}
                                    />
                                </Grid>
                            </Row>

                            <Row sx={{ mb: 0 }}>
                                <Grid sx={{ flex: 1 }}>
                                    <CissTextField
                                        label="Bairro"
                                        name="dsBairro"
                                        value={formik.values.dsBairro}
                                        error={formik.errors.dsBairro}
                                        disabled={isTipoPedidoFuncionario || !formik.values.cep?.cidade?.dsCidade}
                                        onChange={(e) => formik.setFieldValue('dsBairro', e.target.value || null)}
                                    />
                                </Grid>
                                <Grid sx={{ flex: 1 }}>
                                    <CissTextField
                                        disabled
                                        label="Estado"
                                        name="dsEstado"
                                        value={formik.values.cep?.estado?.dsEstado || ''}
                                        error={getIn(formik.errors, 'cep.estado')}
                                    />
                                </Grid>
                                <Grid sx={{ flex: 1 }}>
                                    <CissTextField
                                        disabled
                                        label="Cidade"
                                        name="dsCidade"
                                        value={formik.values.cep?.cidade?.dsCidade || ''}
                                        error={getIn(formik.errors, 'cep.cidade')}
                                    />
                                </Grid>
                            </Row>
                        </Block>
                        <Block>
                            <Row>
                                <Grid sx={{ flex: 1 }}>
                                    <TipoUsuarioAutoComplete
                                        value={formik.values.tipoUsuario}
                                        name="tipoUsuario"
                                        error={formik.errors.tipoUsuario}
                                        onChange={(e, value) => {
                                            formik.setFieldValue('tipoUsuario', value);

                                            if (value?.tipoUsuarioEnum === 'FRANQUEADO') {
                                                formik.setFieldValue('usuarioAcessoGrupoEconomicos', []);
                                                formik.setFieldValue('usuarioAcessoLojas', []);
                                                formik.setFieldValue('usuarioGrupoUsuarios', dataPermissoesPadrao || []);
                                                formik.setFieldValue('loja', null);
                                            } else {
                                                formik.setFieldValue('usuarioGrupoUsuarios', []);
                                            }
                                        }}
                                    />
                                </Grid>
                            </Row>

                            {isFranqueado && (
                                <Row>
                                    <Grid sx={{ flex: 1 }}>
                                        <GrupoEconomicoAutoComplete
                                            multiple
                                            label="Grupos econômicos"
                                            name="usuarioAcessoGrupoEconomicos"
                                            value={formik.values.usuarioAcessoGrupoEconomicos}
                                            error={getIn(formik.errors, 'usuarioAcessoGrupoEconomicos')}
                                            onChange={(e, value) => formik.setFieldValue('usuarioAcessoGrupoEconomicos', value)}
                                        />
                                    </Grid>
                                </Row>
                            )}

                            <Row sx={{ mb: 0 }}>
                                <Grid sx={{ flex: 1 }}>
                                    <GrupoUsuarioAutoComplete
                                        multiple
                                        label="Grupos de usuários"
                                        name="usuarioGrupoUsuarios"
                                        value={formik.values.usuarioGrupoUsuarios}
                                        error={getIn(formik.errors, 'usuarioGrupoUsuarios')}
                                        onChange={(e, value) => formik.setFieldValue('usuarioGrupoUsuarios', value)}
                                    />
                                </Grid>
                            </Row>
                        </Block>

                        {isFuncionario && (
                            <Block>
                                <SubTitle label="Funcionário" />

                                <Row sx={{ mb: 0 }}>
                                    <Grid sx={{ flex: 1 }}>
                                        <LocalFuncionarioAutoComplete
                                            value={formik.values.usuarioFuncionario?.localFuncionario}
                                            disableClearable={true}
                                            name="usuarioFuncionario.localFuncionario"
                                            error={getIn(formik.errors, 'usuarioFuncionario.localFuncionario')}
                                            onChange={(e, value) => formik.setFieldValue('usuarioFuncionario.localFuncionario', value || null)}
                                        />
                                    </Grid>

                                    <Grid sx={{ flex: 1 }}>
                                        <TipoEntregaAutoComplete
                                            value={formik.values.usuarioFuncionario?.tipoEntrega || null}
                                            disableClearable={true}
                                            name="usuarioFuncionario.tipoEntrega"
                                            error={getIn(formik.errors, 'usuarioFuncionario.tipoEntrega')}
                                            onChange={(e, value) => formik.setFieldValue('usuarioFuncionario.tipoEntrega', value)}
                                        />
                                    </Grid>

                                    <Grid sx={{ flex: 1 }}>
                                        <CargoFuncionarioAutoComplete
                                            value={formik.values.usuarioFuncionario?.cargo || null}
                                            name="usuarioFuncionario.cargo"
                                            error={getIn(formik.errors, 'usuarioFuncionario.cargo')}
                                            onChange={(e, value) => formik.setFieldValue('usuarioFuncionario.cargo', value)}
                                        />
                                    </Grid>

                                    <Grid sx={{ flex: 0, display: 'flex', alignItems: 'center' }}>
                                        <Field
                                            as={FormControlLabel}
                                            type="checkbox"
                                            name="usuarioFuncionario.fgAtivoCompra"
                                            control={
                                                <Checkbox
                                                    checked={!formik.values.usuarioFuncionario?.fgAtivoCompra}
                                                    onChange={(e) => formik.setFieldValue('usuarioFuncionario.fgAtivoCompra', !e.target.checked)}
                                                />
                                            }
                                            label="Ausente"
                                        />
                                    </Grid>
                                </Row>
                            </Block>
                        )}

                        {(Boolean(usuarioAcessoGrupoEconomicos?.length) || !isFuncionario) && (
                            <Block>
                                <SubTitle label="Lojas" />

                                {isFranqueado && (
                                    <Row>
                                        <Grid sx={{ flex: 1 }}>
                                            <StoreAutoComplete
                                                label="Lojas"
                                                name="usuarioAcessoLojas"
                                                multiple
                                                defaultStore={false}
                                                importStores={false}
                                                value={formik.values.usuarioAcessoLojas}
                                                onChange={(e, value) => {
                                                    formik.setFieldValue('usuarioAcessoLojas', value);

                                                    if (value.length && formik.values.loja && !value.includes(formik.values.loja)) {
                                                        formik.setFieldValue('loja', null);
                                                    }
                                                }}
                                                requestOptions={{
                                                    url: '/gestao/loja',
                                                    sort: [{ property: 'dsNomeFantasia', direction: 'ASC' }],
                                                    columns: 'idLoja,cdLoja,dsNomeFantasia',
                                                    filter: getFilterLojaGrupoEconomico(usuarioAcessoGrupoEconomicos),
                                                }}
                                            />
                                        </Grid>
                                    </Row>
                                )}

                                <Row sx={{ mb: 0 }}>
                                    <Grid sx={{ flex: 1 }}>
                                        <StoreAutoComplete
                                            label="Loja padrão"
                                            name="loja"
                                            defaultStore={false}
                                            value={formik.values.loja}
                                            error={formik.errors.loja}
                                            options={formik.values.usuarioAcessoLojas.length ? formik.values.usuarioAcessoLojas : undefined}
                                            onChange={(e, value) => formik.setFieldValue('loja', value)}
                                            requestOptions={
                                                formik.values.usuarioAcessoLojas.length
                                                    ? null
                                                    : {
                                                          url: '/gestao/loja',
                                                          sort: [{ property: 'dsNomeFantasia', direction: 'ASC' }],
                                                          columns: 'idLoja,cdLoja,dsNomeFantasia',
                                                          filter: getFilterLojaGrupoEconomico(usuarioAcessoGrupoEconomicos),
                                                      }
                                            }
                                        />
                                    </Grid>
                                </Row>
                            </Block>
                        )}
                    </>
                );
            }}
        </FormPage>
    );
}
